/**
 * DelayedEmailsPanel — shows delayed response emails (preview list + View All modal).
 */

import React, { useMemo, useState } from 'react';
import { Table, Button, Modal, Typography, Tag, Select } from 'antd';
import { AlarmClock, ArrowRight } from 'lucide-react';
import { DELAY_THRESHOLDS, DEFAULT_DELAY_THRESHOLD } from '../constants';

const { Text } = Typography;

function fmtResponseTime(mins) {
  if (mins < 60) return `${mins}m`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

function delayColor(mins) {
  if (mins >= 1440) return 'red';
  if (mins >= 480) return 'orange';
  return 'gold';
}

const columns = [
  {
    title: 'Sender',
    dataIndex: 'from',
    key: 'from',
    ellipsis: true,
    render: (from) => {
      const nameMatch = from.match(/^"?([^"<]+)"?\s*</);
      const name = nameMatch ? nameMatch[1].trim() : null;
      return <Text strong style={{ fontSize: 13 }}>{name || from}</Text>;
    },
  },
  {
    title: 'Email',
    dataIndex: 'email',
    key: 'email',
    ellipsis: true,
    render: (email) => <Text type="secondary" style={{ fontSize: 13 }}>{email}</Text>,
  },
  {
    title: 'Response Time',
    dataIndex: 'responseMin',
    key: 'responseMin',
    width: 140,
    align: 'right',
    render: (mins) => (
      <Tag color={delayColor(mins)} style={{ fontWeight: 600, borderRadius: 6, fontSize: 11 }}>
        {fmtResponseTime(mins)}
      </Tag>
    ),
  },
];

export function DelayedEmailsPanel({ metrics, isDark, onThresholdChange, open, onOpen, onClose }) {
  const [delayThreshold, setDelayThreshold] = useState(DEFAULT_DELAY_THRESHOLD);

  const handleThresholdChange = (val) => {
    const num = Number(val);
    setDelayThreshold(num);
    if (onThresholdChange) onThresholdChange(num);
  };

  const delayed = useMemo(() => {
    if (!metrics?.responseDetails) return [];
    return metrics.responseDetails
      .filter((d) => d.responseMin >= delayThreshold)
      .sort((a, b) => b.responseMin - a.responseMin);
  }, [metrics, delayThreshold]);

  const preview = delayed.slice(0, 5);
  const hasMore = delayed.length > 5;

  const tableData = delayed.map((d, i) => ({ ...d, key: i }));
  const previewData = preview.map((d, i) => ({ ...d, key: i }));

  return (
    <>
      <div style={{
        borderRadius: 14,
        background: isDark ? 'rgba(255,255,255,0.03)' : '#ffffff',
        border: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : '#f0f0f0'}`,
        boxShadow: isDark
          ? '0 1px 3px rgba(0,0,0,0.2)'
          : '0 1px 3px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.02)',
        padding: '20px',
        marginBottom: 24,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{
              width: 32, height: 32, borderRadius: 8,
              background: isDark ? 'rgba(239,68,68,0.15)' : 'rgba(239,68,68,0.08)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <AlarmClock size={16} color="#ef4444" strokeWidth={2} />
            </div>
            <Text strong style={{ fontSize: 14 }}>Delayed Responses</Text>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>Threshold:</Text>
            <Select
              size="small"
              value={delayThreshold}
              onChange={handleThresholdChange}
              style={{ width: 130 }}
              options={DELAY_THRESHOLDS.map((t) => ({ label: t.label, value: t.value }))}
            />
          </div>
        </div>

        {delayed.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '24px 0' }}>
            <Text type="secondary" style={{ fontSize: 13 }}>No delayed responses for this threshold.</Text>
          </div>
        ) : (
          <>
            <Table
              dataSource={previewData}
              columns={columns}
              pagination={false}
              size="small"
              showHeader
            />
            {hasMore && (
              <div style={{ textAlign: 'center', marginTop: 14 }}>
                <Button
                  type="text"
                  onClick={onOpen}
                  style={{
                    fontWeight: 600,
                    fontSize: 12,
                    color: '#ef4444',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                  }}
                >
                  View all {delayed.length} delayed emails
                  <ArrowRight size={13} />
                </Button>
              </div>
            )}
          </>
        )}
      </div>

      <Modal
        open={open}
        onCancel={onClose}
        footer={null}
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <AlarmClock size={16} color="#ef4444" />
            <span>All Delayed Responses</span>
            <Tag color="red" style={{ borderRadius: 6, fontWeight: 600, fontSize: 11, marginLeft: 4 }}>{delayed.length}</Tag>
          </div>
        }
        width={700}
        styles={{ body: { padding: '16px 0 0' } }}
      >
        <Table
          dataSource={tableData}
          columns={columns}
          pagination={{ pageSize: 15, size: 'small', showSizeChanger: false }}
          size="small"
          scroll={{ y: 420 }}
        />
      </Modal>
    </>
  );
}
